import React, { useMemo, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import MockupCard from "../components/MockupCard";
import { TbArrowBackUp } from "react-icons/tb";
import { FaFacebookF, FaInstagram, FaPinterestP } from "react-icons/fa";
import { MdOutlineMailOutline } from "react-icons/md";

import { SiTiktok } from "react-icons/si";
import apiClient from "../api/apiClient";
import { shopApi, foldersApi } from "../api/shopApi";
import LoadingSpinner from "../components/LoadingSpinner";
import InfiniteScroll from "../components/InfiniteScroll";
import NotFound from "../components/NotFound";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import defaultShopBanner from "../assets/images/banner_default.jpg";

const FOLDER_ALL = "all";
const FOLDER_ABOUT = "about";

const AuthorMockups = () => {
  const [searchParams] = useSearchParams();
  const name = searchParams.get("name"); // user_id
  const folderParam = searchParams.get("folder") || FOLDER_ALL;

  const [shop, setShop] = useState(null);
  const [folders, setFolders] = useState([]);
  const [shopLoading, setShopLoading] = useState(true);

  const navigate = useNavigate();

  // Load shop and folders for this seller
  useEffect(() => {
    if (!name) {
      setShopLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setShopLoading(true);
      try {
        const [shopData, foldersList] = await Promise.all([
          shopApi.getByUserId(name),
          foldersApi.listByUserId(name),
        ]);
        if (!cancelled) {
          setShop(shopData || null);
          setFolders(Array.isArray(foldersList) ? foldersList : []);
        }
      } catch (e) {
        if (!cancelled) {
          setShop(null);
          setFolders([]);
        }
      } finally {
        if (!cancelled) setShopLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [name]);

  const hasAbout = Boolean(
    shop && (shop.about_title?.trim() || shop.about_text?.trim() || shop.about_image)
  );

  // Fetch listings + bundles for this seller; optional folder filter. Skip when viewing About.
  const fetchlistdata = useMemo(() => {
    return async (page) => {
      if (folderParam === FOLDER_ABOUT) {
        return { ok: true, data: { results: [], count: 0 } };
      }
      const listParams = new URLSearchParams();
      if (name) listParams.append("user_id", name);
      listParams.append("page_size", "12");
      listParams.append("page", page);
      const folderId = folderParam && folderParam !== FOLDER_ALL ? folderParam : null;
      if (folderId) listParams.append("shop_folder", folderId);
      const bundleParams = new URLSearchParams();
      if (name) bundleParams.append("user_id", name);
      bundleParams.append("page_size", "12");
      bundleParams.append("page", page);
      if (folderId) bundleParams.append("shop_folder", folderId);
      const [listRes, bundleRes] = await Promise.all([
        apiClient.get(`/listings/?${listParams.toString()}`),
        apiClient.get(`/bundle-listings/?calledFrom=home&${bundleParams.toString()}`),
      ]);
      const listings = listRes?.ok ? listRes.data.results || [] : [];
      const bundles = bundleRes?.data?.results || [];
      const combined = [...bundles, ...listings].sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
      const listCount = listRes?.data?.count || 0;
      const bundleCount = bundleRes?.data?.count || 0;
      return {
        ok: listRes?.ok || bundleRes?.ok,
        data: { results: combined, count: listCount + bundleCount },
      };
    };
  }, [name, folderParam]);

  const { data: rawMockups, loading: loader, hasMore, loadMore } = useInfiniteScroll(
    fetchlistdata,
    [name, folderParam],
    12
  );

  const mockupData = rawMockups || [];

  const sellerName = shop?.user_name || mockupData[0]?.user_name || "Seller";
  const usesDefaultBanner = !shop?.banner;

  const setFolder = (folder) => {
    const params = new URLSearchParams(searchParams);
    params.set("name", name);
    if (folder === FOLDER_ALL) params.delete("folder");
    else params.set("folder", folder);
    navigate(`/browse-mockups?${params.toString()}`, { replace: true });
  };

  if (!name) {
    return (
      <section className="py-5">
        <div className="container">
          <NotFound message="No seller selected." underline />
        </div>
      </section>
    );
  }

  const showAbout = folderParam === FOLDER_ABOUT;
  const showMockups = !showAbout;

  return (
    <div className="seller-storefront">
      {/* Padding between top bar and banner; banner same width as content, 1200x400, rounded */}
      <div className="container pt-3 pt-md-4">
        <div className="seller-storefront-banner position-relative rounded-3 overflow-hidden">
          {shopLoading ? (
            <div
              className="seller-banner-placeholder d-flex align-items-center justify-content-center"
              style={{ aspectRatio: "1200/400", minHeight: 180, backgroundColor: "#f0f0f0" }}
            >
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <img
                src={shop?.banner || defaultShopBanner}
                alt="Shop banner"
                className="w-100"
                style={{ aspectRatio: "1200/400", objectFit: "cover", display: "block" }}
              />
              {usesDefaultBanner && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                  style={{ zIndex: 1, pointerEvents: "none" }}
                >
                  <h2 className="mb-0 text-gray-500 text-uppercase fw-bold">
                    {sellerName}
                  </h2>
                </div>
              )}
            </>
          )}
          <div
            className="position-absolute top-0 start-0 p-3"
            style={{ zIndex: 2 }}
          >
            <TbArrowBackUp
              style={{ fontSize: "34px" }}
              className="back-arrow shadow rounded-5 p-1 bg-white"
              onClick={() => navigate(-1)}
            />
          </div>
        </div>
      </div>

      <section className="py-4">
        <div className="container">
          <div className="row">
            {/* Left sidebar: categories (ALL + folders + About) */}
            <aside className="col-lg-3 col-md-4 mb-4 mb-md-0">
              <div className="d-flex flex-md-column flex-wrap align-items-center align-items-md-stretch gap-2 mb-4">
                <div className="d-flex align-items-center gap-2 mb-3 w-100">
                  <Avatar
                    src={shop?.user_profile}
                    sx={{ width: 48, height: 48 }}
                  />
                  <div className="flex-grow-1 min-w-0">
                    <h2 className="h5 fw-bold mb-0 text-truncate">{shop?.user_name || mockupData?.[0]?.user_name || "Seller"}</h2>
                    {/* {shop?.contact_email && (
                      <a href={`mailto:${shop.contact_email}`} className="small text-decoration-none d-block">
                        {shop.contact_email}
                      </a>
                    )}
                    {(shop?.facebook || shop?.instagram || shop?.pinterest || shop?.tiktok) && (
                      <div className="d-flex flex-wrap gap-2 mt-1">
                        {shop?.facebook && (
                          <a href={shop.facebook.startsWith("http") ? shop.facebook : `https://${shop.facebook}`} target="_blank" rel="noopener noreferrer" className="small text-decoration-none">Facebook</a>
                        )}
                        {shop?.instagram && (
                          <a href={shop.instagram.startsWith("http") ? shop.instagram : `https://${shop.instagram}`} target="_blank" rel="noopener noreferrer" className="small text-decoration-none">Instagram</a>
                        )}
                        {shop?.pinterest && (
                          <a href={shop.pinterest.startsWith("http") ? shop.pinterest : `https://${shop.pinterest}`} target="_blank" rel="noopener noreferrer" className="small text-decoration-none">Pinterest</a>
                        )}
                        {shop?.tiktok && (
                          <a href={shop.tiktok.startsWith("http") ? shop.tiktok : `https://${shop.tiktok}`} target="_blank" rel="noopener noreferrer" className="small text-decoration-none">TikTok</a>
                        )}
                      </div>
                    )} */}
                  </div>
                </div>
                <nav className="seller-sidebar-nav w-100">
                  <button
                    type="button"
                    className={`seller-sidebar-item ${folderParam === FOLDER_ALL ? "active" : ""}`}
                    onClick={() => setFolder(FOLDER_ALL)}
                  >
                    All
                  </button>
                  {shopLoading ? (
                    <div className="d-flex justify-content-center py-3">
                      <LoadingSpinner color="#C93312" />
                    </div>
                  ) : (
                    folders.map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        className={`seller-sidebar-item ${String(folderParam) === String(f.id) ? "active" : ""}`}
                        onClick={() => setFolder(String(f.id))}
                      >
                        {f.name}
                      </button>
                    ))
                  )}
                  {hasAbout && (
                    <button
                      type="button"
                      className={`seller-sidebar-item ${folderParam === FOLDER_ABOUT ? "active" : ""}`}
                      onClick={() => setFolder(FOLDER_ABOUT)}
                    >
                      About
                    </button>
                  )}
                </nav>
              </div>
            </aside>

            {/* Main content */}
            <div className="col-lg-9 col-md-8">
              {showAbout && (
                <div
                  className="seller-about-page py-5 px-4 rounded-3 text-center"
                  style={{ backgroundColor: "#E3EBE0" }}
                >
                  {shop?.about_image && (
                    <div className="mb-4 rounded overflow-hidden mx-auto" style={{ maxWidth: 560 }}>
                      <img
                        src={shop.about_image}
                        alt="About"
                        style={{ maxHeight: 320, width: "100%", objectFit: "cover" }}
                      />
                    </div>
                  )}
                  {shop?.about_title?.trim() && (
                    <h1
                      className="seller-about-title fw-bold mb-3"
                      style={{
                        fontSize: "3.5rem",
                        lineHeight: 1.2,
                        color: "#000",
                        textAlign: "center",
                      }}
                    >
                      {shop.about_title}
                    </h1>
                  )}
                  {shop?.about_text?.trim() ? (
                    <p className="large mx-auto mb-0" style={{ maxWidth: 720, whiteSpace: "pre-wrap" }}>
                      {shop.about_text}
                    </p>
                  ) : !shop?.about_title?.trim() ? (
                    <p className="text-muted mb-0">No about text provided.</p>
                  ) : null}

                  {(shop?.contact_email || shop?.facebook || shop?.instagram || shop?.pinterest || shop?.tiktok) && (
                    <div className="seller-about-contact mt-5 pt-4 d-flex flex-column align-items-center gap-2">        
                      {(shop?.contact_email || shop?.facebook || shop?.instagram || shop?.pinterest || shop?.tiktok) && (
                        <div className="d-flex align-items-center justify-content-center gap-3 flex-wrap">
                                {shop?.contact_email && (
                        <a
                          href={`mailto:${shop.contact_email}`}
                          className="text-decoration-none d-flex align-items-center gap-2"
                          style={{ color: "#000", fontSize: "1.5rem" }}
                          >
                          <MdOutlineMailOutline />
                        </a>
                      )}
                          {shop?.facebook && (
                            <a
                              href={shop.facebook.startsWith("http") ? shop.facebook : `https://${shop.facebook}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="seller-about-social"
                              title="Facebook"
                              style={{ color: "#000", fontSize: "1.5rem" }}
                            >
                              <FaFacebookF />
                            </a>
                          )}
                          {shop?.instagram && (
                            <a
                              href={shop.instagram.startsWith("http") ? shop.instagram : `https://${shop.instagram}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="seller-about-social"
                              title="Instagram"
                              style={{ color: "#000", fontSize: "1.5rem" }}
                            >
                              <FaInstagram />
                            </a>
                          )}
                          {shop?.pinterest && (
                            <a
                              href={shop.pinterest.startsWith("http") ? shop.pinterest : `https://${shop.pinterest}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="seller-about-social"
                              title="Pinterest"
                              style={{ color: "#000", fontSize: "1.5rem" }}
                            >
                              <FaPinterestP />
                            </a>
                          )}
                          {shop?.tiktok && (
                            <a
                              href={shop.tiktok.startsWith("http") ? shop.tiktok : `https://${shop.tiktok}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="seller-about-social"
                              title="TikTok"
                              style={{ color: "#000", fontSize: "1.5rem" }}
                            >
                              <SiTiktok />
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {showMockups && (
                <>
                  {loader && mockupData?.length === 0 ? (
                    <div className="d-flex justify-content-center mt-5">
                      <LoadingSpinner />
                    </div>
                  ) : mockupData?.length > 0 ? (
                    <InfiniteScroll onLoadMore={loadMore} hasMore={hasMore} loading={loader}>
                      <div className="row">
                        {mockupData.map((mockup, index) => (
                          <MockupCard
                            key={mockup.id || index}
                            imageUrl={mockup.file}
                            title={mockup.title}
                            description={mockup.short_description}
                            price={mockup.price}
                            seller={mockup.user_name}
                            id={mockup.id}
                            item={mockup}
                            col={4}
                          />
                        ))}
                      </div>
                    </InfiniteScroll>
                  ) : (
                    <NotFound
                      hasActiveSearch={false}
                      emptyMessage={
                        folderParam && folderParam !== FOLDER_ALL && folderParam !== FOLDER_ABOUT
                          ? "No mockups in this folder yet."
                          : undefined
                      }
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .seller-banner-shop-name {
          color: #1a1a1a;
          font-size: clamp(1.25rem, 4vw, 2.5rem);
          letter-spacing: 0.02em;
          text-align: center;
          padding: 0 1rem;
        }
        .seller-sidebar-nav { display: flex; flex-direction: column; gap: 2px; }
        .seller-sidebar-item {
          text-align: left;
          padding: 10px 14px;
          border: none;
          background: transparent;
          border-radius: 8px;
          cursor: pointer;
          font-size: 15px;
          color: #333;
          transition: background 0.2s;
        }
        .seller-sidebar-item:hover { background: #f0f0f0; }
        .seller-sidebar-item.active {
          background: #C93312;
          color: #fff;
        }
        .seller-about-contact .seller-about-email:hover,
        .seller-about-social:hover { opacity: 0.7; }
        .seller-about-social { transition: opacity 0.2s; }
      `}</style>
    </div>
  );
};

export default AuthorMockups;
